import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { TransactionWrapper, Upsell } from '@core/models';
import { TransactionService } from '@core/services/transaction.service';
import { environment } from '@environments/environment';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';

@Component({
  selector: 'app-order-summary',
  imports: [CommonModule],
  templateUrl: './order-summary.component.html',
  styleUrl: './order-summary.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderSummaryComponent {
  private upgradesService = inject(UpgradesService);
  summaryText = input.required<string | null>();
  upsellSelected = input.required<Upsell | null>();
  transactionService = inject(TransactionService);
  rainoutValue = input.required<boolean>();
  totalTransactionSeats = input.required<number>();
  transaction = input.required<TransactionWrapper>();
  isTicketFeeEnabled = input.required<boolean>();
  isDiscountCodesEnabled = input.required<boolean>();
  isProduction = signal(environment.production);

  get serviceChargesDetails() {
    return this.transactionService.getServiceChargesDetails(
      this.transaction().transaction.tdcTransaction!,
      this.transaction()?.client
    );
  }

  get actualServiceCharges() {
    return (
      (this.transaction().transaction?.tdcTransaction?.balance || 0) -
      (this.transaction().transaction?.tdcTransaction?.totalPriceSeats ?? 0)
    );
  }

  get actualServiceChargesAmountWithoutInsuranceAndUpsell() {
    let amount =
      this.actualServiceCharges -
      (this.transaction().transaction?.tdcTransaction?.insuranceRainoutPrice ||
        0);

    const transactionUpsell = this.transactionService.getTransactionUpsell();
    if (transactionUpsell) {
      const transactionUpsellPrice =
        (transactionUpsell.price ?? 0) * this.totalTransactionSeats();
      amount = amount - transactionUpsellPrice;
    }

    return amount > 0 ? amount : 0;
  }

  get acquiredUpgrades() {
    return this.upgradesService.acquiredUpgrades;
  }

  get hasActiveDiscount(): boolean {
    const tdc = this.transaction()?.transaction?.tdcTransaction;
    return this.transactionService.hasActiveDiscount(tdc);
  }

  get discountCode(): string | null {
    return this.transactionService.getDiscountCode(
      this.transaction()?.transaction
    );
  }

  get totalDiscount(): number | null {
    const tdc = this.transaction()?.transaction?.tdcTransaction;
    return this.transactionService.getTotalDiscount(tdc);
  }

  get seats() {
    const map = new Map<
      number,
      { price: number; quantity: number; total: number }
    >();

    const seatsPricesSource = this.transactionService.getSeatsPricesForDisplay(
      this.transaction()
    );

    for (const seat of Object.values(seatsPricesSource)) {
      if (map.has(seat.value)) {
        map.get(seat.value)!.quantity += 1;
        map.get(seat.value)!.total += seat.value;
      } else {
        map.set(seat.value, {
          price: seat.value,
          quantity: 1,
          total: seat.value,
        });
      }
    }

    return Array.from(map.values());
  }

  get totalPriceSeats() {
    const tdc = this.transaction()?.transaction?.tdcTransaction;
    const transactionUpsell = this.transactionService.getTransactionUpsell();
    const upsellPrice = transactionUpsell?.price ?? 0;
    const totalSeats = this.transactionService.getTotalSeats();

    return this.transactionService.getTotalPriceSeatsForDisplay(
      tdc,
      upsellPrice,
      totalSeats
    );
  }
}
